import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tab,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  Typography,
  useTheme,
  Table,
  TextField,
  Chip,
  Tooltip,
  Checkbox,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axiosInstance";
import ReportGroupUnits from "./reportGroupUnits";
import ReportGroupSubs from "./reportGroupSubs";
import ReportGroupReports from "./ReportGroupReports";

function TabPanel({ children, value, index }) {
  return value === index ? <Box>{children}</Box> : null;
}

const ReportGroupDetails = ({ group, onBack }) => {
  const theme = useTheme();
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [groupUnits, setGroupUnits] = useState([]);
  const [groupSubs, setGroupSubs] = useState([]);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Unit Name");
  const [searchQuery, setSearchQuery] = useState("");

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const fetchUnits = async () => {
    if (!group?.id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/devices?groupId=${group.id}`
      );
      setGroupUnits(response.data || []);
    } catch (err) {
      console.error("Error fetching units:", err);
      setError("Failed to load units.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubs = async () => {
    if (!group?.id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/subscribers?groupId=${group.id}`
      );
      setGroupSubs(response.data || []);
    } catch (err) {
      console.error("Error fetching subscribers:", err);
      setError("Failed to load subscribers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnits();
    fetchSubs();
  }, [group?.id]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedUnitsData = useMemo(() => {
    if (!groupUnits) return [];

    return [...groupUnits].sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case "Unit Name":
          aValue = a.name;
          bValue = b.name;
          break;
        case "Modem Serial Number":
          aValue = a.id;
          bValue = b.id;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [groupUnits, order, orderBy]);

  const filteredUnitsData = sortedUnitsData.filter(
    (row) =>
      row.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedSubsData = useMemo(() => {
    if (!groupSubs) return [];

    return [...groupSubs].sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case "Name":
          aValue = a.name;
          bValue = b.name;
          break;
        case "Email":
          aValue = a.id;
          bValue = b.id;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [groupSubs, order, orderBy]);

  const filteredSubsData = sortedSubsData.filter(
    (row) =>
      row.user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderUnitsTable = () => (
    <TableContainer
      sx={{
        flexGrow: 1,
        height: "63vh",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
            {["Unit Name", "Modem Serial Number", "Delete"].map((col) => (
              <TableCell key={col} sx={{ fontSize: 15 }}>
                <TableSortLabel
                  active={orderBy === col}
                  direction={orderBy === col ? order : "asc"}
                  onClick={() => handleRequestSort(col)}
                >
                  {col}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredUnitsData.length > 0 ? (
            filteredUnitsData.map((unit, index) => (
              <TableRow
                key={unit.id}
                hover
                sx={{
                  backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                  "&:hover": {
                    backgroundColor: "#4a4d54",
                  },
                }}
              >
                <TableCell sx={{ fontSize: 15 }}>{unit.name}</TableCell>
                <TableCell sx={{ fontSize: 15 }}>{unit.id}</TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    // onClick={() => handleDeleteClick(grp)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                No units found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderSubsTable = () => (
    <TableContainer
      sx={{
        flexGrow: 1,
        height: "63vh",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
            {["Name", "Email", "Daily", "Weekly", "Monthly", "Delete"].map(
              (col) => (
                <TableCell key={col} sx={{ fontSize: 15 }}>
                  <TableSortLabel
                    active={orderBy === col}
                    direction={orderBy === col ? order : "asc"}
                    onClick={() => handleRequestSort(col)}
                  >
                    {col}
                  </TableSortLabel>
                </TableCell>
              )
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredSubsData.length > 0 ? (
            filteredSubsData.map((sub, index) => (
              <TableRow
                key={sub.id}
                hover
                sx={{
                  backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                  "&:hover": {
                    backgroundColor: "#4a4d54",
                  },
                }}
              >
                <TableCell sx={{ fontSize: 15 }}>{sub.user.name}</TableCell>
                <TableCell sx={{ fontSize: 15 }}>{sub.user.email}</TableCell>
                <TableCell sx={{ fontSize: 15 }}>
                  <Checkbox
                    checked={sub.daily}
                    onChange={() => console.log("first")}
                    // disabled={updatingUsers.has(
                    //   `${member.id}-smsEnable`
                    // )}
                    size={window.innerWidth < 600 ? "small" : "medium"}
                    sx={{
                      transform: {
                        xs: "scale(0.8)",
                        sm: "scale(0.9)",
                        md: "scale(1)",
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: 15 }}>
                  <Checkbox
                    checked={sub.weekly}
                    // onChange={() =>
                    //   handleSmsToggle(member.id, member.smsEnable)
                    // }
                    // disabled={updatingUsers.has(
                    //   `${member.id}-smsEnable`
                    // )}
                    size={window.innerWidth < 600 ? "small" : "medium"}
                    sx={{
                      transform: {
                        xs: "scale(0.8)",
                        sm: "scale(0.9)",
                        md: "scale(1)",
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: 15 }}>
                  <Checkbox
                    checked={sub.monthly}
                    // onChange={() =>
                    //   handleSmsToggle(member.id, member.smsEnable)
                    // }
                    // disabled={updatingUsers.has(
                    //   `${member.id}-smsEnable`
                    // )}
                    size={window.innerWidth < 600 ? "small" : "medium"}
                    sx={{
                      transform: {
                        xs: "scale(0.8)",
                        sm: "scale(0.9)",
                        md: "scale(1)",
                      },
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    // onClick={() => handleDeleteClick(grp)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                No subscribers found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box
      sx={{
        width: "100%",
        p: 2,
        color: "white",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={onBack} sx={{ color: "white", mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography>
              Report Group: {group?.name || "Group Details"}
            </Typography>
            <Chip
              label={`${group.devices} units`}
              color="primary"
              size="small"
            />
          </Box>
        </Box>
      </Box>

      <Paper sx={{ p: 1, backgroundColor: "transparent", minHeight: "73vh" }}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          sx={{
            backgroundColor: "transparent",
          }}
        >
          <Tab label="Units" />
          <Tab label="Subscribers" />
          <Tab label="Reports" />
        </Tabs>

        <TabPanel value={tabIndex} index={0}>
          <ReportGroupUnits group={group} />
        </TabPanel>

        <TabPanel value={tabIndex} index={1}>
          <ReportGroupSubs group={group} />
        </TabPanel>
        <TabPanel value={tabIndex} index={2}>
          <ReportGroupReports group={group} />
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default ReportGroupDetails;
